import { fetchEventSource } from '@fortaine/fetch-event-source';
import { logger, sanitizeError } from '@heidi/runtime/logger';
import type {
  CreateExampleViaChatRequest,
  TemplateCreationRecordInitialGenerationRequest,
  UpdateStructureFromExampleRequest,
  UpdateStructureViaChatRequest,
} from '../../contracts';
import {
  TEMPLATE_CREATION_STREAM_WIRE_FORMAT_HEADER,
  TEMPLATE_CREATION_STREAM_WIRE_FORMAT_V1,
  templateStreamEnvelopeSchema,
  type StreamDeltaPayload,
} from './templateStreamEnvelope';
import { TemplateStructureStreamError } from './templateStructureStreamError';
import { getTemplateStreamTransport } from './transport';
import type { StreamType } from './types';

export const isTemplateStreamV1Negotiated = (): boolean =>
  !getTemplateStreamTransport().isOnPremRuntime();

function buildTemplateStreamHeaders(): Record<string, string> {
  const { buildScribeSseHeaders, getAuthTokenForHttpClientSync } =
    getTemplateStreamTransport();
  const token = getAuthTokenForHttpClientSync();
  // On-prem gateways allow only essential CORS request headers, so the v1
  // wire-format opt-in is omitted there — the backend then serves the legacy
  // `{data}` chunks, which templateStreamEnvelopeSchema still accepts.
  const templateStreamHeaders = isTemplateStreamV1Negotiated()
    ? {
        [TEMPLATE_CREATION_STREAM_WIRE_FORMAT_HEADER]:
          TEMPLATE_CREATION_STREAM_WIRE_FORMAT_V1,
      }
    : undefined;
  return buildScribeSseHeaders(token, templateStreamHeaders);
}

function dispatchTemplateStreamDataLine(
  rawMessage: string,
  handlers: {
    onMessage?: (streamData: StreamType) => void;
    onStreamErrorFrame?: (frame: { error: string; message?: string }) => void;
    onDone?: () => void;
    onUnparseableFrame?: (rawMessage: string) => void;
  },
  logPrefix: string
): void {
  if (!rawMessage.trim()) {
    return;
  }
  const streamParseResult = templateStreamEnvelopeSchema.safeParse(rawMessage);
  if (!streamParseResult.success) {
    // PHI: `rawMessage` is the verbatim SSE frame and carries generated
    // template structure and example-note clinical text, and a zod error
    // embeds the received value in its issue messages. Log the shape instead —
    // an issue's `path` and `code` are schema vocabulary, and a length is a
    // count (`.ai-context/telemetry-phi-pii-safety.md`: length and shape,
    // never the value). The unparsed frame still reaches the in-app handler
    // below, so nothing the caller observes changes.
    logger.error(`${logPrefix} stream parse error`, {
      issues: streamParseResult.error.issues.map((issue) => ({
        path: issue.path.join('.'),
        code: issue.code,
      })),
      rawMessageLength: rawMessage.length,
    });
    handlers.onUnparseableFrame?.(rawMessage);
    return;
  }
  const data = streamParseResult.data;
  try {
    if ('kind' in data) {
      if (data.kind === 'error') {
        handlers.onStreamErrorFrame?.({
          error: data.error,
          message: data.message,
        });
        return;
      }
      if (data.kind === 'done') {
        handlers.onDone?.();
        return;
      }
      const payload: StreamDeltaPayload = { data: data.text };
      handlers.onMessage?.(payload);
      return;
    }
    const payload: StreamDeltaPayload = { data: data.data };
    handlers.onMessage?.(payload);
  } catch (error) {
    if (error instanceof TemplateStructureStreamError) {
      throw error;
    }
    logger.error(`${logPrefix} stream message handler error`, error);
  }
}

export interface TemplateStreamEventHandlers {
  onOpen?: (response: Response) => Promise<void> | void;
  onMessage?: (streamData: StreamType) => void;
  /** Structured error frame (Option C); not used for all stream entrypoints */
  onStreamErrorFrame?: (frame: { error: string; message?: string }) => void;
  /** Terminal v1 `done` frame. Legacy `{data}` streams never emit this. */
  onDone?: () => void;
  /** Counted by conversion completeness; other consumers may ignore it. */
  onUnparseableFrame?: (rawMessage: string) => void;
  onClose?: () => void;
  onError?: (error: unknown) => void;
}

export interface TemplateStreamConfig {
  data: TemplateCreationRecordInitialGenerationRequest;
  templateCreationRecordId: string;
  targetVersionControlId: string;
  signal: AbortSignal;
  logPrefix?: string;
}

/**
 * Streams a template creation request and handles the response
 * @param config - Configuration for the streaming request (must include signal for abort control)
 * @param handlers - Event handlers for stream events
 */
export function streamInitialStructureGeneration(
  config: TemplateStreamConfig,
  handlers: TemplateStreamEventHandlers = {}
): Promise<void> {
  const { data, signal, logPrefix = '[TEMPLATE_CREATION_STREAM]' } = config;

  const {
    onOpen,
    onMessage,
    onClose,
    onError,
    onStreamErrorFrame,
    onDone,
    onUnparseableFrame,
  } = handlers;

  const requestHeaders = buildTemplateStreamHeaders();

  return fetchEventSource(
    buildInitialStructureGenerationRequestPath({
      templateCreationRecordId: config.templateCreationRecordId,
      targetVersionControlId: config.targetVersionControlId,
    }),
    {
      headers: requestHeaders,
      openWhenHidden: true,
      method: 'POST',
      body: JSON.stringify(data),
      signal,
      onopen: (response) =>
        getTemplateStreamTransport().handleStreamOpen({
          response,
          logPrefix,
          onOpen,
          onError,
          context: { templateCreationRecordId: config.templateCreationRecordId },
        }),
      onmessage(ev) {
        dispatchTemplateStreamDataLine(
          ev.data,
          { onMessage, onStreamErrorFrame, onDone, onUnparseableFrame },
          logPrefix
        );
      },
      onclose() {
        logger.debug(`${logPrefix} stream closed`);
        try {
          onClose?.();
        } catch (error) {
          logger.error(`${logPrefix} onClose handler error`, error);
        }
      },
      onerror(err) {
        logger.error(`${logPrefix} stream error`, sanitizeError(err));
        try {
          onError?.(err as unknown);
        } catch (error) {
          logger.error(`${logPrefix} onError handler error`, error);
        }
        throw err; // throw error to stop the stream from retrying
      },
    }
  );
}

export const buildInitialStructureGenerationRequestPath = ({
  templateCreationRecordId,
  targetVersionControlId,
}: {
  templateCreationRecordId: string;
  targetVersionControlId: string;
}) => {
  return `${getTemplateStreamTransport().getDomain('v2')}templates/template-creation-record/${templateCreationRecordId}/version-control/${targetVersionControlId}/generate-initial`;
};

export interface GenerateExampleFromStructureRequest {
  template_creation_record_id: string;
  version_control_id: string;
}

export interface ExampleNoteStreamConfig {
  data: GenerateExampleFromStructureRequest;
  signal: AbortSignal;
  logPrefix?: string;
}

/**
 * Streams an example note generation request and handles the response
 * @param config - Configuration for the streaming request (must include signal for abort control)
 * @param handlers - Event handlers for stream events
 */
export function streamExampleFromStructureGeneration(
  config: ExampleNoteStreamConfig,
  handlers: TemplateStreamEventHandlers = {}
): Promise<void> {
  const { data, signal, logPrefix = '[EXAMPLE_NOTE_STREAM]' } = config;

  const {
    onOpen,
    onMessage,
    onClose,
    onError,
    onStreamErrorFrame,
    onDone,
    onUnparseableFrame,
  } = handlers;

  const requestHeaders = buildTemplateStreamHeaders();

  return fetchEventSource(
    buildExampleFromStructureRequestPath(
      data.template_creation_record_id,
      data.version_control_id
    ),
    {
      headers: requestHeaders,
      openWhenHidden: true,
      method: 'POST',
      signal,
      onopen: (response) =>
        getTemplateStreamTransport().handleStreamOpen({
          response,
          logPrefix,
          onOpen,
          onError,
          context: { templateCreationRecordId: data.template_creation_record_id },
        }),
      onmessage(ev) {
        dispatchTemplateStreamDataLine(
          ev.data,
          { onMessage, onStreamErrorFrame, onDone, onUnparseableFrame },
          logPrefix
        );
      },
      onclose() {
        logger.debug(`${logPrefix} stream closed`);
        try {
          onClose?.();
        } catch (error) {
          logger.error(`${logPrefix} onClose handler error`, error);
        }
      },
      onerror(err) {
        logger.error(`${logPrefix} stream error`, sanitizeError(err));
        try {
          onError?.(err as unknown);
        } catch (error) {
          logger.error(`${logPrefix} onError handler error`, error);
        }
        throw err; // throw error to stop the stream from retrying
      },
    }
  );
}

export const buildExampleFromStructureRequestPath = (
  templateCreationRecordId: string,
  versionControlId: string
) => {
  return `${getTemplateStreamTransport().getDomain('v2')}templates/template-creation-record/${templateCreationRecordId}/${versionControlId}/example-note`;
};

export interface StructureViaChatStreamConfig {
  data: UpdateStructureViaChatRequest;
  templateCreationRecordId: string;
  targetVersionControlId: string;
  signal: AbortSignal;
  logPrefix?: string;
}

/**
 * Streams a structure generation via chat request and handles the response
 * @param config - Configuration for the streaming request (must include signal for abort control)
 * @param handlers - Event handlers for stream events
 */
export function streamStructureViaChatGeneration(
  config: StructureViaChatStreamConfig,
  handlers: TemplateStreamEventHandlers = {}
): Promise<void> {
  const { data, signal, logPrefix = '[STRUCTURE_VIA_CHAT_STREAM]' } = config;

  const {
    onOpen,
    onMessage,
    onClose,
    onError,
    onStreamErrorFrame,
    onDone,
    onUnparseableFrame,
  } = handlers;

  const requestHeaders = buildTemplateStreamHeaders();

  return fetchEventSource(
    buildStructureViaChatRequestPath({
      templateCreationRecordId: config.templateCreationRecordId,
      targetVersionControlId: config.targetVersionControlId,
    }),
    {
      headers: requestHeaders,
      openWhenHidden: true,
      method: 'POST',
      body: JSON.stringify(data),
      signal,
      onopen: (response) =>
        getTemplateStreamTransport().handleStreamOpen({
          response,
          logPrefix,
          onOpen,
          onError,
          context: { templateCreationRecordId: config.templateCreationRecordId },
        }),
      onmessage(ev) {
        dispatchTemplateStreamDataLine(
          ev.data,
          { onMessage, onStreamErrorFrame, onDone, onUnparseableFrame },
          logPrefix
        );
      },
      onclose() {
        logger.debug(`${logPrefix} stream closed`);
        try {
          onClose?.();
        } catch (error) {
          logger.error(`${logPrefix} onClose handler error`, error);
        }
      },
      onerror(err) {
        logger.error(`${logPrefix} stream error`, sanitizeError(err));
        try {
          onError?.(err as unknown);
        } catch (error) {
          logger.error(`${logPrefix} onError handler error`, error);
        }
        throw err; // throw error to stop the stream from retrying
      },
    }
  );
}

export const buildStructureViaChatRequestPath = ({
  templateCreationRecordId,
  targetVersionControlId,
}: {
  templateCreationRecordId: string;
  targetVersionControlId: string;
}) => {
  return `${getTemplateStreamTransport().getDomain('v2')}templates/template-creation-record/${templateCreationRecordId}/version-control/${targetVersionControlId}/chat/structure`;
};

export interface ExampleViaChatStreamConfig {
  data: CreateExampleViaChatRequest;
  templateCreationRecordId: string;
  targetVersionControlId: string;
  signal: AbortSignal;
  logPrefix?: string;
}

/**
 * Streams an example update via chat request and handles the response
 * @param config - Configuration for the streaming request (must include signal for abort control)
 * @param handlers - Event handlers for stream events
 */
export function streamExampleViaChatGeneration(
  config: ExampleViaChatStreamConfig,
  handlers: TemplateStreamEventHandlers = {}
): Promise<void> {
  const { data, signal, logPrefix = '[EXAMPLE_VIA_CHAT_STREAM]' } = config;

  const {
    onOpen,
    onMessage,
    onClose,
    onError,
    onStreamErrorFrame,
    onDone,
    onUnparseableFrame,
  } = handlers;

  const requestHeaders = buildTemplateStreamHeaders();

  return fetchEventSource(
    buildExampleViaChatRequestPath({
      templateCreationRecordId: config.templateCreationRecordId,
      targetVersionControlId: config.targetVersionControlId,
    }),
    {
      headers: requestHeaders,
      openWhenHidden: true,
      method: 'POST',
      body: JSON.stringify(data),
      signal,
      onopen: (response) =>
        getTemplateStreamTransport().handleStreamOpen({
          response,
          logPrefix,
          onOpen,
          onError,
          context: { templateCreationRecordId: config.templateCreationRecordId },
        }),
      onmessage(ev) {
        dispatchTemplateStreamDataLine(
          ev.data,
          { onMessage, onStreamErrorFrame, onDone, onUnparseableFrame },
          logPrefix
        );
      },
      onclose() {
        logger.debug(`${logPrefix} stream closed`);
        try {
          onClose?.();
        } catch (error) {
          logger.error(`${logPrefix} onClose handler error`, error);
        }
      },
      onerror(err) {
        logger.error(`${logPrefix} stream error`, sanitizeError(err));
        try {
          onError?.(err as unknown);
        } catch (error) {
          logger.error(`${logPrefix} onError handler error`, error);
        }
        throw err; // throw error to stop the stream from retrying
      },
    }
  );
}

export const buildExampleViaChatRequestPath = ({
  templateCreationRecordId,
  targetVersionControlId,
}: {
  templateCreationRecordId: string;
  targetVersionControlId: string;
}) => {
  return `${getTemplateStreamTransport().getDomain('v2')}templates/template-creation-record/${templateCreationRecordId}/version-control/${targetVersionControlId}/chat/example-note`;
};

export interface UpdateStructureFromNoteStreamConfig {
  data: UpdateStructureFromExampleRequest;
  templateCreationRecordId: string;
  targetVersionControlId: string;
  signal: AbortSignal;
  logPrefix?: string;
}

/**
 * Streams a structure update from note request and handles the response
 * @param config - Configuration for the streaming request (must include signal for abort control)
 * @param handlers - Event handlers for stream events
 */
export function streamUpdateStructureFromNote(
  config: UpdateStructureFromNoteStreamConfig,
  handlers: TemplateStreamEventHandlers = {}
): Promise<void> {
  const { data, signal, logPrefix = '[UPDATE_STRUCTURE_FROM_NOTE_STREAM]' } = config;

  const {
    onOpen,
    onMessage,
    onClose,
    onError,
    onStreamErrorFrame,
    onDone,
    onUnparseableFrame,
  } = handlers;

  const requestHeaders = buildTemplateStreamHeaders();

  return fetchEventSource(
    buildUpdateStructureFromNoteRequestPath({
      templateCreationRecordId: config.templateCreationRecordId,
      targetVersionControlId: config.targetVersionControlId,
    }),
    {
      headers: requestHeaders,
      openWhenHidden: true,
      method: 'POST',
      body: JSON.stringify(data),
      signal,
      onopen: (response) =>
        getTemplateStreamTransport().handleStreamOpen({
          response,
          logPrefix,
          onOpen,
          onError,
          context: { templateCreationRecordId: config.templateCreationRecordId },
        }),
      onmessage(ev) {
        dispatchTemplateStreamDataLine(
          ev.data,
          { onMessage, onStreamErrorFrame, onDone, onUnparseableFrame },
          logPrefix
        );
      },
      onclose() {
        logger.debug(`${logPrefix} stream closed`);
        try {
          onClose?.();
        } catch (error) {
          logger.error(`${logPrefix} onClose handler error`, error);
        }
      },
      onerror(err) {
        logger.error(`${logPrefix} stream error`, sanitizeError(err));
        try {
          onError?.(err as unknown);
        } catch (error) {
          logger.error(`${logPrefix} onError handler error`, error);
        }
        throw err; // throw error to stop the stream from retrying
      },
    }
  );
}

export const buildUpdateStructureFromNoteRequestPath = ({
  templateCreationRecordId,
  targetVersionControlId,
}: {
  templateCreationRecordId: string;
  targetVersionControlId: string;
}) => {
  return `${getTemplateStreamTransport().getDomain('v2')}templates/template-creation-record/${templateCreationRecordId}/version-control/${targetVersionControlId}/example-note/structure`;
};
